import { Dispatch } from 'redux';
import { NavigateFunction } from 'react-router-dom';
import { cloneDeep } from 'lodash';
import { saveAs } from 'file-saver';

import { RootState } from './store';
import * as api from '../utils/api';
import { ModuleTypes } from '../utils/constants';
import { dataURLtoBlob, notifyError, processModuleAvailability } from '../utils/functions';

interface LearnerState {
  course?: ICourse;
  loading?: boolean;
  moduleLoading?: boolean;
  showReviewRetake?: boolean;
  currentSlideId?: string;
}

const actions = {
  UPDATE: 'lms-ui/learner/UPDATE',
  UPDATE_SLIDE_ID: 'lms-ui/learner/UPDATE_SLIDE_ID',
  GETTING_DATA: 'lms-ui/learner/GETTING_DATA',
  MODULE_LOADING: 'lms-ui/learner/MODULE_LOADING',
};

interface LearnerAction {
  type: typeof actions.GETTING_DATA;
  payload: LearnerState | string | boolean;
}

type LearnerActionTypes = LearnerAction;

const initialState: LearnerState = {
  course: undefined,
  loading: false,
  moduleLoading: false,
  showReviewRetake: false,
};

/** Learner Ducks */
const update = (payload: LearnerState) => {
  return {
    type: actions.UPDATE,
    payload,
  };
};

const commonGetLearnerModule =
  ({
    courseId,
    moduleId,
    initialLoad,
    navigate,
  }: {
    courseId: string;
    moduleId: string;
    initialLoad?: boolean;
    navigate?: NavigateFunction;
  }) =>
  async (dispatch: Dispatch) => {
    try {
      const response = await api.getLearnerModule({ courseId, moduleId });
      const course = response.data as ICourse;
      const module = course?.modules?.find((m) => m._id === moduleId);
      if (initialLoad && [ModuleTypes.Document, ModuleTypes.Embedded].includes(module?.type)) {
        dispatch(markLearnerViewed({ courseId, moduleId }) as any);
      } else {
        const updatedData: any = { course, moduleLoading: false };
        if (initialLoad) {
          updatedData.showReviewRetake = module?.showReviewRetake;
        }

        if (module?.type !== ModuleTypes.Author) {
          updatedData.loading = false;
        }

        return dispatch(update(updatedData));
      }
    } catch (e) {
      notifyError(e, dispatch);
      if (navigate) {
        navigate(`/courses/${courseId}`);
      }
    }
  };

export const getLearnerCourse =
  ({ courseId }: { courseId: string }) =>
  async (dispatch: Dispatch) => {
    try {
      dispatch({ type: actions.GETTING_DATA });
      const response = await api.getLearnerCourse(courseId);
      const course = response.data as ICourse;
      dispatch(update({ course, currentSlideId: undefined, loading: false }));
    } catch (e) {
      notifyError(e, dispatch);
    }
  };

export const getLearnerModule =
  ({
    courseId,
    moduleId,
    navigate,
  }: {
    courseId: string;
    moduleId: string;
    navigate: NavigateFunction;
  }) =>
  async (dispatch: Dispatch) => {
    try {
      dispatch({ type: actions.GETTING_DATA });
      dispatch({ type: actions.MODULE_LOADING });
      dispatch<any>(commonGetLearnerModule({ courseId, moduleId, initialLoad: true, navigate }));
    } catch (e) {
      notifyError(e, dispatch);
    }
  };

export const markLearnerViewed =
  ({ courseId, moduleId }: { courseId: string; moduleId: string }) =>
  async (dispatch: Dispatch) => {
    try {
      await api.markLearnerViewed({ courseId, moduleId });
      dispatch<any>(commonGetLearnerModule({ courseId, moduleId }));
    } catch (e) {
      notifyError(e, dispatch);
    }
  };

export const setLearnerCompleted =
  ({ courseId, moduleId }: { courseId: string; moduleId: string }) =>
  async (dispatch: Dispatch) => {
    try {
      await api.setLearnerCompleted({ courseId, moduleId });
      return dispatch<any>(commonGetLearnerModule({ courseId, moduleId }));
    } catch (e) {
      notifyError(e, dispatch);
    }
  };

export const getLearnerModuleNext =
  ({
    courseId,
    moduleId,
    navigate,
  }: {
    courseId: string;
    moduleId: string;
    navigate: NavigateFunction;
  }) =>
  async (dispatch: Dispatch) => {
    try {
      const response = await api.setLearnerCompleted({ courseId, moduleId });
      const course = response.data as ICourse;
      let route = '/';
      const { modules } = course;
      if (modules) {
        // Build module availability list
        const moduleAvailability = processModuleAvailability(course);
        const currentModuleIndex = modules.findIndex((m) => m._id === moduleId);
        if (currentModuleIndex > -1) {
          let moduleId: string | undefined;
          // First check if subsequent modules are available
          for (let i = currentModuleIndex + 1; i < moduleAvailability.length; i++) {
            const availability = moduleAvailability[i];
            if (availability.isAvailable) {
              moduleId = availability.module._id;
              break;
            }
          }

          // Otherwise check if prior modules are available and hasn't been completed
          if (!moduleId) {
            for (let i = 0; i < currentModuleIndex; i++) {
              const availability = moduleAvailability[i];
              if (availability.isAvailable && !availability.module.completed) {
                moduleId = availability.module._id;
                break;
              }
            }
          }

          if (moduleId) {
            route = `/courses/${courseId}/modules/${moduleId}`;
          }
        }
      }

      navigate(route);
    } catch (e) {
      notifyError(e, dispatch);
    }
  };

export const resetLearnerModule =
  ({ courseId, moduleId }: { courseId: string; moduleId: string; navigate: NavigateFunction }) =>
  async (dispatch: Dispatch) => {
    try {
      await api.resetLearnerModule({ courseId, moduleId });
      return dispatch<any>(commonGetLearnerModule({ courseId, moduleId }));
    } catch (e) {
      notifyError(e, dispatch);
    }
  };

export const updateShowReviewRetake = (show: boolean) => async (dispatch: Dispatch) => {
  try {
    dispatch(update({ showReviewRetake: false }));
  } catch (e) {
    notifyError(e, dispatch);
  }
};

export const updateSlideData =
  ({ courseId, moduleId, slideData }: { courseId: string; moduleId: string; slideData: ISlide }) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    try {
      const state = getState();
      const course = cloneDeep(state.learner.course);
      if (course?._id === courseId) {
        let modified = false;
        if ((slideData as any)?.completed) {
          // TODO: Look into timing of this, may need to speed it up somehow
          const response = await api.getLearnerCourseProgress(courseId);
          course.progress = response.data.progress;
          modified = true;
        }

        const module = course?.modules.find((m) => m._id === moduleId);
        // This will only be true for linear, non-test Author Modules
        if (module?.slides) {
          const index = module.slides.findIndex((s) => s._id === slideData._id) ?? -1;
          if (index > -1) {
            const updatedSlide = cloneDeep(module.slides[index]);
            if (!updatedSlide.progress) {
              // Need to cast as any here because the default
              // object is expected required fields like moduleProgressId
              // which we don't currently have
              updatedSlide.progress = {} as any;
            }

            // Need to cast slideData as any here otherwise there's a field type conflict
            // for slideId
            updatedSlide.progress = { ...updatedSlide.progress, ...(slideData as any) };
            module.slides.splice(index, 1, updatedSlide);
            modified = true;
          }
        }

        if (modified) {
          dispatch(update({ course }));
        }
      }
    } catch (e) {
      notifyError(e, dispatch);
    }
  };

export const updateSlideId =
  ({ courseId, slideId }: { courseId: string; slideId?: string }) =>
  async (dispatch: Dispatch, getState: () => RootState) => {
    try {
      const state = getState();
      if (state.learner.course?._id === courseId) {
        dispatch({ type: actions.UPDATE_SLIDE_ID, payload: slideId });
      }
    } catch (e) {
      notifyError(e, dispatch);
    }
  };

export const clearLearnerCourse = () => async (dispatch: Dispatch) => {
  dispatch(update({ course: undefined, currentSlideId: undefined }));
};

export const updateLearnerLoading = (loading: boolean) => async (dispatch: Dispatch) => {
  dispatch(update({ loading }));
};

export const getLearnerCertificate =
  ({ courseId, title }: { courseId: string; title?: string }) =>
  async (dispatch: Dispatch) => {
    try {
      const response = await api.getLearnerCertificate(courseId);
      const blob = new Blob([response.data], { type: 'octet/stream' });
      saveAs(blob, `Praktio Certificate${title ? ` - ${title}` : ''}.pdf`);
    } catch (e) {
      notifyError(e, dispatch);
    }

    return Promise.resolve();
  };

export const submitLearnerFeedback =
  ({
    message,
    screenshot,
    courseId,
    moduleId,
    slideId,
    errorMessage,
  }: {
    message: string;
    screenshot?: string;
    courseId?: string;
    moduleId?: string;
    slideId?: string;
    errorMessage?: string;
  }) =>
  async (dispatch: Dispatch) => {
    try {
      const data = new FormData();
      data.append('message', message);
      if (screenshot) {
        data.append('screenshot', dataURLtoBlob(screenshot), 'screenshot.png');
      }

      if (courseId) {
        data.append('courseId', courseId);
      }

      if (moduleId) {
        data.append('moduleId', moduleId);
      }

      if (slideId) {
        data.append('slideId', slideId);
      }

      if (errorMessage) {
        data.append('errorMessage', errorMessage);
      }

      await api.submitLearnerFeedback(data);
    } catch (e) {
      notifyError(e, dispatch);
    }
  };

export const updateLearnerStatus =
  ({
    courseId,
    moduleId,
    lastActive,
    eventType,
  }: {
    courseId: string;
    moduleId: string;
    lastActive: Date;
    eventType?: string;
  }) =>
  async (dispatch: Dispatch) => {
    try {
      await api.updateLearnerStatus({
        courseId,
        moduleId,
        lastActive,
        eventType,
      });
    } catch (e) {
      notifyError(e, dispatch);
    }
  };

export default function reducer(state = initialState, action: LearnerActionTypes): LearnerState {
  switch (action.type) {
    case actions.UPDATE: {
      return {
        ...state,
        ...(action.payload as LearnerState),
      };
    }
    case actions.UPDATE_SLIDE_ID: {
      return {
        ...state,
        currentSlideId: action.payload as string,
      };
    }
    case actions.GETTING_DATA: {
      return {
        ...state,
        loading: true,
      };
    }
    case actions.MODULE_LOADING: {
      return {
        ...state,
        moduleLoading: true,
      };
    }
    default:
      return state;
  }
}
